import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Stack,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Divider,
  CircularProgress,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  CloudUpload,
  Download,
  Feedback,
  ExpandMore,
  ExpandLess,
  Refresh,
  Assessment,
  History as HistoryIcon,
  MoreVert,
  Upload,
  Timeline,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
  Key as KeyIcon
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import { supabase, User, isSupabaseConfigured } from './lib/supabase';
import { API_ENDPOINTS } from './config';
import { History } from './components/History';
import { Corrections } from './components/Corrections';
// import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

// Styled components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  border: `2px dashed ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.background.default,
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.dark,
    backgroundColor: theme.palette.action.hover,
  },
}));

// Authentication Context
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set a maximum loading timeout (30 seconds)
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
    }, 30000);

    // Get initial session with timeout and error handling
    const getInitialSession = async () => {
      try {
        // Check if Supabase is properly configured
        if (!isSupabaseConfigured()) {
          clearTimeout(loadingTimeout);
          setLoading(false);
          return;
        }

        // Set a timeout for the session check
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session check timeout')), 10000)
        );

        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]) as any;

        if (error) {
          clearTimeout(loadingTimeout);
          setLoading(false);
          return;
        }
        
        if (session?.user) {
          try {
            // Fetch user profile from profiles table with timeout
            const profilePromise = supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .single();
            
            const profileTimeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
            );

            const { data: profile } = await Promise.race([
              profilePromise,
              profileTimeoutPromise
            ]) as any;

            const userData: User = {
              id: session.user.id,
              email: session.user.email || '',
              full_name: profile?.full_name || session.user.user_metadata?.full_name,
              avatar_url: profile?.avatar_url || session.user.user_metadata?.avatar_url,
            };

            setUser(userData);
            setIsAuthenticated(true);
          } catch (profileError) {
            // Still authenticate with basic user data
            const userData: User = {
              id: session.user.id,
              email: session.user.email || '',
              full_name: session.user.user_metadata?.full_name,
              avatar_url: session.user.user_metadata?.avatar_url,
            };
            setUser(userData);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        // Silent error handling for production
      } finally {
        clearTimeout(loadingTimeout);
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes with error handling
    let subscription: any;
    try {
      const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
        async (event: any, session: any) => {
          try {
            if (session?.user) {
              try {
                // Fetch user profile from profiles table with timeout
                const profilePromise = supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', session.user.id)
                  .single();
                
                const profileTimeoutPromise = new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
                );

                const { data: profile } = await Promise.race([
                  profilePromise,
                  profileTimeoutPromise
                ]) as any;

                const userData: User = {
                  id: session.user.id,
                  email: session.user.email || '',
                  full_name: profile?.full_name || session.user.user_metadata?.full_name,
                  avatar_url: profile?.avatar_url || session.user.user_metadata?.avatar_url,
                };

                setUser(userData);
                setIsAuthenticated(true);
              } catch (profileError) {
                // Still authenticate with basic user data
                const userData: User = {
                  id: session.user.id,
                  email: session.user.email || '',
                  full_name: session.user.user_metadata?.full_name,
                  avatar_url: session.user.user_metadata?.avatar_url,
                };
                setUser(userData);
                setIsAuthenticated(true);
              }
            } else {
              setUser(null);
              setIsAuthenticated(false);
            }
          } catch (error) {
            setUser(null);
            setIsAuthenticated(false);
          } finally {
            setLoading(false);
          }
        }
      );
      subscription = authSubscription;
    } catch (error) {
      setLoading(false);
    }

    return () => {
      clearTimeout(loadingTimeout);
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Page Component
const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const success = await login(email, password);
      if (!success) {
        setError('Invalid email or password. Please check your credentials.');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom color="primary">
            🏭 Meter Data Classifier
          </Typography>
          <Typography variant="h5" gutterBottom>
            Sign In
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please sign in with your authorized credentials to continue.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ mb: 2 }}
            startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Don't have access? Contact your administrator to get an account.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

// Password Change Dialog Component
interface PasswordChangeDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const PasswordChangeDialog: React.FC<PasswordChangeDialogProps> = ({ 
  open, 
  onClose, 
  onSuccess, 
  onError 
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    current: '',
    new: '',
    confirm: '',
    general: ''
  });

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswords({ current: false, new: false, confirm: false });
    setErrors({ current: '', new: '', confirm: '', general: '' });
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors = { current: '', new: '', confirm: '', general: '' };
    let isValid = true;

    if (!currentPassword) {
      newErrors.current = 'Current password is required';
      isValid = false;
    }

    if (!newPassword) {
      newErrors.new = 'New password is required';
      isValid = false;
    } else if (newPassword.length < 6) {
      newErrors.new = 'Password must be at least 6 characters long';
      isValid = false;
    }

    if (!confirmPassword) {
      newErrors.confirm = 'Please confirm your new password';
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirm = 'Passwords do not match';
      isValid = false;
    }

    if (currentPassword && newPassword && currentPassword === newPassword) {
      newErrors.new = 'New password must be different from current password';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({ current: '', new: '', confirm: '', general: '' });

    try {
      // First verify current password by attempting to re-authenticate
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: (await supabase.auth.getUser()).data.user?.email || '',
        password: currentPassword,
      });

      if (signInError) {
        setErrors(prev => ({ ...prev, current: 'Current password is incorrect' }));
        setLoading(false);
        return;
      }

      // Update password using the Supabase method
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        setErrors(prev => ({ ...prev, general: error.message || 'Failed to update password' }));
        setLoading(false);
        return;
      }

      // Success
      onSuccess('Password updated successfully!');
      handleClose();

    } catch (error: any) {
      setErrors(prev => ({ 
        ...prev, 
        general: error.message || 'An unexpected error occurred' 
      }));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyIcon color="primary" />
          Change Password
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.general}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Current Password"
            type={showPasswords.current ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            error={!!errors.current}
            helperText={errors.current}
            margin="normal"
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                  edge="end"
                >
                  {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />

          <TextField
            fullWidth
            label="New Password"
            type={showPasswords.new ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={!!errors.new}
            helperText={errors.new || 'Password must be at least 6 characters long'}
            margin="normal"
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                  edge="end"
                >
                  {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            type={showPasswords.confirm ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!!errors.confirm}
            helperText={errors.confirm}
            margin="normal"
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                  edge="end"
                >
                  {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <KeyIcon />}
        >
          {loading ? 'Updating...' : 'Update Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Header Component with Auth
const AuthHeader: React.FC = () => {
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const handlePasswordChange = () => {
    setPasswordDialogOpen(true);
    handleClose();
  };

  const handlePasswordSuccess = (message: string) => {
    setSnackbar({ open: true, message, severity: 'success' });
  };

  const handlePasswordError = (message: string) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            🏭 Meter Data Classifier
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">
              Welcome, {user?.full_name || user?.email}
            </Typography>
            <IconButton
              size="large"
              onClick={handleMenu}
              color="inherit"
            >
              <PersonIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handlePasswordChange}>
                <ListItemIcon>
                  <KeyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Password</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <PasswordChangeDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        onSuccess={handlePasswordSuccess}
        onError={handlePasswordError}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        message={snackbar.message}
      />
    </>
  );
};

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={48} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Checking authentication...
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          If this takes too long, please refresh the page
        </Typography>
      </Box>
    );
  }

  // If Supabase is not configured, show a warning but allow access
  if (!isSupabaseConfigured()) {
    return (
      <Box>
        <Alert severity="info" sx={{ m: 2 }}>
          <Typography variant="h6">Authentication Not Available</Typography>
          <Typography variant="body2">
            Authentication is currently not configured. The application will work without user accounts.
          </Typography>
        </Alert>
        {children}
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <>{children}</>;
};

// Type definitions
interface ProcessingStatus {
  task_id: string;
  status: string;
  progress: number;
  message: string;
  file_info?: {
    filename: string;
    size: number;
  };
  results?: PredictionResult[];
  error?: string;
}

interface PredictionResult {
  prediction_id: string;
  input_data: string;
  parsed_columns?: {
    'Pi tag Name'?: string;
    'Asset Type'?: string;
    'Issue Description'?: string;
    'Data Collection Type'?: string;
    'Frequency'?: string;
    'PI Tag Type'?: string;
    'PI Tag Description'?: string;
    [key: string]: any;
  };
  status: string;
  data_status: string;
  confidence: number;
  timestamp: string;
  examples_used_count: number;
  best_similarity: number;
}

interface AccuracyStats {
  total_predictions: number;
  correct_predictions: number;
  accuracy_percentage: number;
  by_category: Record<string, { total: number; correct: number; accuracy: number }>;
  feedback_count: number;
}

// Main App Component
function MainApp() {
  // Page navigation state
  const [currentPage, setCurrentPage] = useState<'processing' | 'history' | 'corrections'>('processing');
  
  // State management
  const [file, setFile] = useState<File | null>(null);
  const [currentTask, setCurrentTask] = useState<ProcessingStatus | null>(null);
  const [predictions, setPredictions] = useState<PredictionResult[]>([]);
  const [accuracyStats, setAccuracyStats] = useState<AccuracyStats | null>(null);
  const [feedbackDialog, setFeedbackDialog] = useState<{
    open: boolean;
    prediction?: PredictionResult;
  }>({ open: false });
  const [expandedResults, setExpandedResults] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });
  const [historyDialog, setHistoryDialog] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Use refs for polling to prevent infinite loops
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);

  // Utility function to map data status for display
  const mapDataStatusForDisplay = (dataStatus: string) => {
    if (dataStatus.toLowerCase() === 'no issues') {
      return 'Resolved';
    }
    return dataStatus;
  };

  // Utility function to map column headers for display
  const mapColumnHeaderForDisplay = (columnName: string) => {
    if (columnName === 'Data Collection Type') {
      return 'Issue Type';
    }
    return columnName;
  };

  // Start polling function
  const startPolling = (taskId: string) => {
    if (isPollingRef.current) {
      return;
    }
    
    isPollingRef.current = true;
    
    pollingIntervalRef.current = setInterval(async () => {
        try {
        const statusUrl = `${API_ENDPOINTS.PROCESSING_STATUS}/${taskId}`;
        
        const response = await fetch(statusUrl);
        
          if (response.ok) {
            const status = await response.json();
          
            setCurrentTask(status);
            
            if (status.status === 'completed') {
              // Set predictions from the results when processing is completed
              setPredictions(status.results || []);
              stopPolling();
              setSnackbar({
                open: true,
                message: 'Processing completed successfully!',
                severity: 'success'
              });
            } else if (status.status === 'error') {
              stopPolling();
              setSnackbar({
                open: true,
                message: `Processing failed: ${status.error}`,
                severity: 'error'
              });
            }
        } else {
          }
        } catch (error) {
        }
      }, 2000);
  };

  // Stop polling function
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingRef.current = false;
  };

  // Cleanup polling on unmount
  useEffect(() => {
      return () => {
      stopPolling();
      };
  }, []);

  // Load accuracy stats
  useEffect(() => {
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
      
      if (!validExtensions.includes(fileExtension)) {
        setSnackbar({
          open: true,
          message: 'Please select a valid file (.xlsx, .xls, or .csv)',
          severity: 'error'
        });
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleUploadAndProcess = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(API_ENDPOINTS.UPLOAD_PROCESS, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        
        const newTask = {
          task_id: result.task_id,
          status: 'processing',
          progress: 0,
          message: 'Starting processing...',
          file_info: { filename: file.name, size: file.size }
        };
        
        setCurrentTask(newTask);
        
        // Start polling for this task
        startPolling(result.task_id);
        
        setSnackbar({
          open: true,
          message: 'File uploaded successfully. Processing started...',
          severity: 'info'
        });
      } else {
        let errorMessage = 'Upload failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // If parsing JSON fails, use default message
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to upload file',
        severity: 'error'
      });
    }
  };

  const handleFeedbackSubmit = async (isCorrect: boolean, correctedStatus?: string, correctedDataStatus?: string, feedbackText?: string) => {
    if (!feedbackDialog.prediction) return;

    try {
      const response = await fetch(API_ENDPOINTS.FEEDBACK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prediction_id: feedbackDialog.prediction.prediction_id,
          is_correct: isCorrect,
          corrected_status: correctedStatus,
          corrected_data_status: correctedDataStatus,
          feedback_text: feedbackText
        })
      });

      if (response.ok) {
        const feedbackType = isCorrect ? 'positive' : 'corrective';
        setSnackbar({
          open: true,
          message: `${feedbackType.charAt(0).toUpperCase() + feedbackType.slice(1)} feedback submitted successfully! The model will learn from this input.`,
          severity: 'success'
        });
        setFeedbackDialog({ open: false });
      } else {
        let errorMessage = 'Feedback submission failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // If parsing JSON fails, use default message
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to submit feedback',
        severity: 'error'
      });
    }
  };

  const downloadResults = async (format: 'excel' | 'json' = 'excel') => {
    if (!currentTask) return;

    try {
      const endpoint = format === 'excel' 
        ? `${API_ENDPOINTS.DOWNLOAD_EXCEL}/${currentTask.task_id}/excel`
        : `${API_ENDPOINTS.DOWNLOAD_PREDICTIONS}/${currentTask.task_id}/download`;
      
      const response = await fetch(endpoint);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `predictions_${currentTask.task_id}.${format === 'excel' ? 'xlsx' : 'json'}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        
        setSnackbar({
          open: true,
          message: `Results downloaded as ${format.toUpperCase()} successfully!`,
          severity: 'success'
        });
      } else {
        throw new Error(`Download failed: ${response.statusText}`);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to download ${format.toUpperCase()} results`,
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'no issues':
      case 'resolved/no issues':
        return 'success';
      case 'issues':
      case 'ongoing':
        return 'error';
      case 'recurring late data':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <AuthHeader />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom color="primary">
            AI-Powered Classification of Meter Data Issues
          </Typography>
          
          {/* Navigation Tabs */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Tabs
              value={currentPage}
              onChange={(_, newValue) => setCurrentPage(newValue)}
              centered
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                label="Process Files" 
                value="processing" 
                icon={<Upload />}
                iconPosition="start"
              />
              <Tab 
                label="History" 
                value="history" 
                icon={<Timeline />}
                iconPosition="start"
              />
              <Tab 
                label="Corrections" 
                value="corrections" 
                icon={<Assessment />}
                iconPosition="start"
              />
            </Tabs>
          </Box>
        </Paper>

        {/* Page Content */}
        {currentPage === 'processing' && <ProcessingPage 
          file={file}
          setFile={setFile}
          currentTask={currentTask}
          predictions={predictions}
          expandedResults={expandedResults}
          setExpandedResults={setExpandedResults}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          handleFileChange={handleFileChange}
          handleUploadAndProcess={handleUploadAndProcess}
          downloadResults={downloadResults}
          getStatusColor={getStatusColor}
          getConfidenceColor={getConfidenceColor}
          setFeedbackDialog={setFeedbackDialog}
          mapDataStatusForDisplay={mapDataStatusForDisplay}
          mapColumnHeaderForDisplay={mapColumnHeaderForDisplay}
        />}

        {currentPage === 'history' && <HistoryPage />}

        {currentPage === 'corrections' && <CorrectionsPage />}

        {/* Feedback Dialog */}
        <FeedbackDialog
          open={feedbackDialog.open}
          prediction={feedbackDialog.prediction}
          onClose={() => setFeedbackDialog({ open: false })}
          onSubmit={handleFeedbackSubmit}
          mapDataStatusForDisplay={mapDataStatusForDisplay}
          mapColumnHeaderForDisplay={mapColumnHeaderForDisplay}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          message={snackbar.message}
        />
      </Container>
    </>
  );
}

// Feedback Dialog Component
interface FeedbackDialogProps {
  open: boolean;
  prediction?: PredictionResult;
  onClose: () => void;
  onSubmit: (isCorrect: boolean, correctedStatus?: string, correctedDataStatus?: string, feedbackText?: string) => void;
  mapDataStatusForDisplay: (dataStatus: string) => string;
  mapColumnHeaderForDisplay: (columnName: string) => string;
}

function FeedbackDialog({ open, prediction, onClose, onSubmit, mapDataStatusForDisplay, mapColumnHeaderForDisplay }: FeedbackDialogProps) {
  const [isCorrect, setIsCorrect] = useState<string>('');
  const [correctedStatus, setCorrectedStatus] = useState('');
  const [correctedDataStatus, setCorrectedDataStatus] = useState('');
  const [feedbackText, setFeedbackText] = useState('');

  const handleSubmit = () => {
    const correct = isCorrect === 'yes';
    onSubmit(
      correct,
      correct ? undefined : correctedStatus,
      correct ? undefined : correctedDataStatus,
      feedbackText || undefined
    );
    
    // Reset form
    setIsCorrect('');
    setCorrectedStatus('');
    setCorrectedDataStatus('');
    setFeedbackText('');
  };

  if (!prediction) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Provide Feedback for Prediction</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Predicted Status:
          </Typography>
          <Chip label={prediction.status} color="primary" />
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Predicted Data Status:
          </Typography>
          <Chip label={mapDataStatusForDisplay(prediction.data_status)} color="primary" />
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Confidence: {(prediction.confidence * 100).toFixed(1)}%
          </Typography>
        </Box>

        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Is this prediction correct?</FormLabel>
          <RadioGroup
            value={isCorrect}
            onChange={(e) => setIsCorrect(e.target.value)}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes, the prediction is correct" />
            <FormControlLabel value="no" control={<Radio />} label="No, the prediction is incorrect" />
          </RadioGroup>
        </FormControl>

        {isCorrect === 'no' && (
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Correct Status"
              value={correctedStatus}
              onChange={(e) => setCorrectedStatus(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Correct Data Status"
              value={correctedDataStatus}
              onChange={(e) => setCorrectedDataStatus(e.target.value)}
              sx={{ mb: 2 }}
            />
          </Box>
        )}

        <TextField
          fullWidth
          label="Additional Feedback (Optional)"
          multiline
          rows={3}
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!isCorrect}
        >
          Submit Feedback
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ProcessingPage Component
interface ProcessingPageProps {
  file: File | null;
  setFile: (file: File | null) => void;
  currentTask: ProcessingStatus | null;
  predictions: PredictionResult[];
  expandedResults: Set<string>;
  setExpandedResults: (expanded: Set<string>) => void;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUploadAndProcess: () => void;
  downloadResults: (format: 'excel' | 'json') => void;
  getStatusColor: (status: string) => 'success' | 'error' | 'warning' | 'default';
  getConfidenceColor: (confidence: number) => 'success' | 'warning' | 'error';
  setFeedbackDialog: (dialog: { open: boolean; prediction?: PredictionResult }) => void;
  mapDataStatusForDisplay: (dataStatus: string) => string;
  mapColumnHeaderForDisplay: (columnName: string) => string;
}

function ProcessingPage({
  file,
  setFile,
  currentTask,
  predictions,
  expandedResults,
  setExpandedResults,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  handleFileChange,
  handleUploadAndProcess,
  downloadResults,
  getStatusColor,
  getConfidenceColor,
  setFeedbackDialog,
  mapDataStatusForDisplay,
  mapColumnHeaderForDisplay
}: ProcessingPageProps) {
  return (
    <>
      {/* File Upload Section */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          📁 Upload Data File
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Upload your data file (Excel .xlsx/.xls or CSV .csv) to process and get predictions.
        </Typography>

        <UploadBox>
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Drop your data file here or click to browse
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUpload />}
              sx={{ mr: 2 }}
            >
              Choose File
              <VisuallyHiddenInput
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
              />
            </Button>
            <Button
              variant="contained"
              disabled={!file || (currentTask?.status === 'processing')}
              onClick={handleUploadAndProcess}
              startIcon={currentTask?.status === 'processing' ? <CircularProgress size={20} /> : undefined}
            >
              {currentTask?.status === 'processing' ? 'Processing...' : 'Upload & Process'}
            </Button>
          </Box>
          {file && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </Alert>
          )}
        </UploadBox>
      </Paper>

      {/* Processing Status */}
      {currentTask && (
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            ⚙️ Processing Status
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body1" component="span">
                Status:
            </Typography>
              <Chip label={currentTask.status} color={currentTask.status === 'completed' ? 'success' : currentTask.status === 'error' ? 'error' : 'primary'} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {currentTask.message}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={currentTask.progress} 
            sx={{ height: 10, borderRadius: 5, mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {currentTask.progress}% complete
          </Typography>
          
          {currentTask.status === 'error' && currentTask.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {currentTask.error}
            </Alert>
          )}
        </Paper>
      )}

      {/* Results Section */}
      {predictions.length > 0 && (
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">
               Prediction Results ({predictions.length} items)
            </Typography>
            <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Download />}
                onClick={() => downloadResults('excel')}
            >
                Download Excel
            </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => downloadResults('json')}
              >
                Download JSON
              </Button>
            </Stack>
          </Box>

          <TableContainer sx={{ width: '100%', overflow: 'auto' }}>
            <Table sx={{ minWidth: 2000 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 350 }}>PI Tag Name</TableCell>
                  <TableCell sx={{ minWidth: 300 }}>Tag Description</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Asset Type</TableCell>
                  <TableCell sx={{ minWidth: 300 }}>Issue Description</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Predicted Status</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>Predicted Data Status</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Prediction ID</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {predictions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((prediction) => (
                    <React.Fragment key={prediction.prediction_id}>
                      <TableRow hover>
                        <TableCell sx={{ minWidth: 350 }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: '0.875rem' }}>
                            {prediction.parsed_columns?.['Pi tag Name'] || 'N/A'}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newExpanded = new Set(expandedResults);
                              if (newExpanded.has(prediction.prediction_id)) {
                                newExpanded.delete(prediction.prediction_id);
                              } else {
                                newExpanded.add(prediction.prediction_id);
                              }
                              setExpandedResults(newExpanded);
                            }}
                          >
                            {expandedResults.has(prediction.prediction_id) ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ minWidth: 300 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            {(prediction.parsed_columns?.['PI Tag Description'] || 'N/A').substring(0, 150)}
                            {((prediction.parsed_columns?.['PI Tag Description']?.length || 0) > 150) && '...'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <Chip
                            label={prediction.parsed_columns?.['Asset Type'] || 'Unknown'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 300 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            {(prediction.parsed_columns?.['Issue Description'] || 'N/A').substring(0, 120)}
                            {((prediction.parsed_columns?.['Issue Description']?.length || 0) > 120) && '...'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Chip
                            label={prediction.status}
                            color={getStatusColor(prediction.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Chip
                            label={mapDataStatusForDisplay(prediction.data_status)}
                            color={getStatusColor(prediction.data_status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {prediction.prediction_id.substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <Tooltip title="Provide Feedback">
                            <IconButton
                              color="primary"
                              onClick={() => setFeedbackDialog({ open: true, prediction })}
                            >
                              <Feedback />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0 }}>
                          <Collapse in={expandedResults.has(prediction.prediction_id)}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Full Details:
                              </Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 2, mb: 2 }}>
                                {prediction.parsed_columns && Object.entries(prediction.parsed_columns).map(([key, value]) => (
                                  <Box key={key}>
                                    <Typography variant="caption" color="text.secondary">
                                      {mapColumnHeaderForDisplay(key)}:
                              </Typography>
                                    <Typography variant="body2">
                                      {value || 'N/A'}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="body2">
                                  Confidence: {(prediction.confidence * 100).toFixed(1)}%
                                </Typography>
                                <Typography variant="body2">
                                  Examples Used: {prediction.examples_used_count || 0}
                                </Typography>
                                <Typography variant="body2">
                                  Best Similarity: {
                                    prediction.best_similarity != null && !isNaN(prediction.best_similarity) 
                                      ? `${(prediction.best_similarity * 100).toFixed(1)}%` 
                                      : 'N/A'
                                  }
                                </Typography>
                                <Typography variant="body2">
                                  Timestamp: {new Date(prediction.timestamp).toLocaleString()}
                                </Typography>
                                <Typography variant="body2">
                                  Full Prediction ID: {prediction.prediction_id}
                                </Typography>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={predictions.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}
    </>
  );
}

// HistoryPage Component
function HistoryPage() {
  return (
    <Paper elevation={3} sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        📊 Processing History
          </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View your previous processing sessions and download results.
          </Typography>
      
      {/* Use the existing History component but embedded in the page */}
      <History 
        open={true} 
        onClose={() => {}} 
        embedded={true}
        />
    </Paper>
  );
}

// CorrectionsPage Component
function CorrectionsPage() {
  return (
    <Paper elevation={3} sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        📋 Corrections
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review and manage corrections for predictions.
      </Typography>
      
      {/* Use the existing Corrections component but embedded in the page */}
      <Corrections />
    </Paper>
  );
}

// Root App Component
function App() {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <MainApp />
      </ProtectedRoute>
    </AuthProvider>
  );
}

export default App;